// @ts-nocheck
/**
 * Modal that surfaces results from `verifyCircuit()` before a Run.
 *
 *   - Errors  → blocking: user must pick Run Anyway or Cancel.
 *   - Warnings → non-blocking summary so the user can still review them
 *     even when there are no errors (we don't render the modal in that
 *     case — a setMessage banner does the job).
 *
 * The component is dumb: it just renders the verification result it's
 * given. The caller (EditorToolbar) decides when to show it.
 */
import type {
  CircuitWarning,
  VerificationResult,
} from "@/lib/velxio/simulation/verify/circuitVerifier";

interface Props {
  result: VerificationResult;
  onCancel: () => void;
  onRunAnyway: () => void;
}

const SEVERITY_COPY: Record<
  CircuitWarning["severity"],
  { label: string; color: string }
> = {
  error: { label: "Error", color: "#e74c3c" },
  warning: { label: "Warning", color: "#f1c40f" },
};

export const CircuitVerificationModal: React.FC<Props> = ({
  result,
  onCancel,
  onRunAnyway,
}) => {
  const items: CircuitWarning[] = [...result.errors, ...result.warnings];
  return (
    <div style={styles.overlay} onClick={onCancel}>
      <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
        <h2 style={styles.title}>Circuit verification</h2>
        <p style={styles.subtitle}>
          {result.errors.length === 0
            ? `${result.warnings.length} warning${result.warnings.length === 1 ? "" : "s"} found.`
            : `${result.errors.length} error${result.errors.length === 1 ? "" : "s"} and ${result.warnings.length} warning${result.warnings.length === 1 ? "" : "s"} found. Running anyway is at your own risk.`}
        </p>
        <ul style={styles.list}>
          {items.map((it, i) => (
            <li key={i} style={styles.row}>
              <span
                style={{
                  ...styles.badge,
                  background: SEVERITY_COPY[it.severity].color,
                }}
              >
                {SEVERITY_COPY[it.severity].label}
              </span>
              <span style={styles.message}>
                {it.componentId ? (
                  <strong style={styles.componentId}>{it.componentId}: </strong>
                ) : null}
                {it.message}
              </span>
            </li>
          ))}
        </ul>
        <div style={styles.actions}>
          <button onClick={onCancel} style={styles.primaryBtn}>
            Cancel
          </button>
          <button onClick={onRunAnyway} style={styles.secondaryBtn}>
            Run anyway
          </button>
        </div>
      </div>
    </div>
  );
};

const styles: Record<string, React.CSSProperties> = {
  overlay: {
    position: "fixed",
    inset: 0,
    background: "rgba(0,0,0,.65)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 1000,
  },
  modal: {
    background: "#252526",
    border: "1px solid #3c3c3c",
    borderRadius: 8,
    padding: "1.5rem",
    width: 560,
    maxHeight: "80vh",
    overflowY: "auto",
    display: "flex",
    flexDirection: "column",
    gap: 14,
  },
  title: { color: "#eee", margin: 0, fontSize: 18, fontWeight: 600 },
  subtitle: { color: "#aaa", margin: 0, fontSize: 13, lineHeight: 1.4 },
  list: {
    listStyle: "none",
    margin: 0,
    padding: 0,
    display: "flex",
    flexDirection: "column",
    gap: 8,
  },
  row: {
    display: "flex",
    alignItems: "flex-start",
    gap: 10,
    padding: "8px 10px",
    background: "#1e1e1e",
    border: "1px solid #333",
    borderRadius: 4,
    fontSize: 13,
    lineHeight: 1.4,
    color: "#ddd",
  },
  badge: {
    color: "#1e1e1e",
    fontSize: 11,
    fontWeight: 700,
    padding: "2px 8px",
    borderRadius: 3,
    textTransform: "uppercase",
    letterSpacing: 0.4,
    flexShrink: 0,
    marginTop: 1,
  },
  message: { flex: 1 },
  componentId: { color: "#7ec5f0", fontFamily: "monospace" },
  actions: {
    display: "flex",
    justifyContent: "flex-end",
    gap: 8,
    marginTop: 4,
  },
  primaryBtn: {
    background: "#0e639c",
    color: "#fff",
    border: "none",
    padding: "8px 16px",
    borderRadius: 4,
    cursor: "pointer",
    fontSize: 13,
    fontWeight: 500,
  },
  secondaryBtn: {
    background: "transparent",
    color: "#ccc",
    border: "1px solid #555",
    padding: "8px 16px",
    borderRadius: 4,
    cursor: "pointer",
    fontSize: 13,
  },
};
